import React from 'react';
import { Table as AntDTable } from 'antd';
import { ColumnsType, ColumnType, TableProps as AntDTableProps } from 'antd/lib/table';
import isEmpty from 'lodash/isEmpty';
import debounce from 'lodash/debounce';
import classNames from 'classnames';
import { LoadingOutlined } from '@ant-design/icons';

import { Maybe, PagedData, PagingData } from 'types';
import { defaultPagingData } from 'components/constants';
import { Button, Select, Input } from 'components/AntD';
import { massageTableColumns } from './utils';
import { Search } from 'Icons';
import slColors from 'sl-colors';
import './styles.less';

// eslint-disable-next-line @typescript-eslint/ban-types
export interface TableProps<RecordType extends object> extends AntDTableProps<RecordType> {
  data: Maybe<PagedData<RecordType>>;
  disableSearch?: boolean;
  enableFilterColumn?: boolean;
  placeholder?: string;
  minHtmlSize?: number;
  children?: React.ReactNode;
  tableActions?: React.ReactNode;
  beforeSearchInputTableActions?: React.ReactNode;
  pagingData?: PagingData;
  setPagingData?(pagingData: PagingData): void;
  tableRef?: React.RefCallback<HTMLTableElement>;
  disabledColumnsDataIndex?: string[];
  disabledPagination?: boolean;
  onSearchInput?: (keyword?: string) => void;
  beforeTableComponent?: React.ReactNode;
}

// eslint-disable-next-line @typescript-eslint/ban-types
export default function Table<RecordType extends object>(
  props: TableProps<RecordType>
): JSX.Element {
  const {
    columns: baseColumns,
    data,
    children,
    tableRef,
    beforeSearchInputTableActions,
    tableActions,
    setPagingData,
    loading,
    onRow,
    disableSearch: _disableSearch,
    className = '',
    pagination = {},
    pagingData = defaultPagingData,
    enableFilterColumn = false,
    placeholder = 'Search',
    disabledColumnsDataIndex,
    disabledPagination = false,
    minHtmlSize,
    onSearchInput,
    beforeTableComponent = null,
    ...rest
  } = props;
  const [keyword, setKeyword] = React.useState(pagingData?.keyword || '');
  const columns = React.useMemo(
    () => massageTableColumns(baseColumns!, disabledColumnsDataIndex),
    [baseColumns, disabledColumnsDataIndex]
  );

  const { disableSearch = !props.pagingData } = props;
  const [filteredColumns, setFilteredColumns] = React.useState<ColumnsType<RecordType>>([]);
  React.useEffect(() => {
    // If we receive new columns, we should reset the filtered columns
    setFilteredColumns([...(columns as Array<ColumnType<RecordType>>)]);
  }, [columns]);

  const ref = React.useRef<HTMLDivElement>();

  React.useEffect(() => {
    if (tableRef) {
      tableRef(ref.current?.querySelector('table') as HTMLTableElement);
    }
  }, [tableRef]);

  const debouncedOnChange = React.useMemo(
    () =>
      debounce((updated: PagingData) => {
        setPagingData && setPagingData(updated);
        onSearchInput && onSearchInput(updated.keyword);
      }, 500),
    [setPagingData, onSearchInput]
  );

  return (
    <div
      className={classNames('sl-antd-table', className)}
      ref={ref as React.LegacyRef<HTMLDivElement>}
    >
      {(!disableSearch || children || tableActions || enableFilterColumn) && (
        <div className="tw-rounded tw-mb-5 tw-bg-gray-100 tw-px-3 tw-py-2 tw-flex tw-gap-2 tw-items-center">
          {beforeSearchInputTableActions}
          {!disableSearch && (
            <Input
              id="main-search-input"
              className={classNames({
                'tw-flex-grow tw-min-w-0': !children && !(tableActions || enableFilterColumn),
              })}
              htmlSize={minHtmlSize || placeholder?.length}
              placeholder={placeholder}
              suffix={<Search color={slColors.gray[300]} />}
              onChange={e => {
                setKeyword(e.target.value);
                debouncedOnChange({
                  ...pagingData,
                  page: 1,
                  keyword: e.target.value,
                });
              }}
              value={keyword}
              allowClear
            />
          )}
          {children}
          {(tableActions || enableFilterColumn) && <span className="tw-flex-grow tw-min-w-0" />}
          {tableActions}
          {enableFilterColumn && (
            <Select<ColumnType<RecordType>>
              placement="bottomRight"
              id="filter-columns"
              idKey="key"
              labelKey="title"
              mode="multiple"
              contents={columns as Array<ColumnType<RecordType>>}
              onChange={(filtered: Array<ColumnType<RecordType>>) => {
                setFilteredColumns(filtered);
              }}
              selected={filteredColumns}
              label={
                <Button
                  className="tw-text-gray-600 hover:tw-text-primary-400"
                  icon={<i className="fas fa-ellipsis-v" />}
                />
              }
              disableSearch
            />
          )}
        </div>
      )}
      {beforeTableComponent}
      <AntDTable<RecordType>
        className="tw-overflow-x-auto"
        onRow={(data, index) => {
          const res = onRow ? onRow(data, index) : {};
          return {
            ...res,
            className: classNames(res.className, {
              'tw-cursor-pointer': res.onClick,
            }),
          };
        }}
        onChange={(_pagination, _filters, sorter, { action }) => {
          if (setPagingData && action === 'sort') {
            const { field, order } = sorter as { field: string; order: string };

            setPagingData({
              ...pagingData,
              page: 1,
              sortDirection: order === 'ascend' ? 'asc' : 'desc',
              sortOrder: order === 'ascend' ? 'asc' : 'desc',
              sortField: field,
            });
          }
        }}
        pagination={
          props.pagingData && !disabledPagination
            ? {
                showSizeChanger: true,
                position: ['bottomCenter'],
                total: (data as PagedData<RecordType>)?.totalCount || 0,
                current: pagingData.page,
                pageSize: pagingData.pageSize,
                showTotal: (total, range) => `Showing ${range[0]} — ${range[1]} of ${total}`,
                className:
                  'ant-table-pagination ant-table-pagination-center tw-flex tw-flex-row tw-my-5 tw-box-content tw-mx-auto tw-text-sm tw-text-gray tw-bg-gray-100 tw-py-1 tw-px-3 tw-rounded',
                onChange: setPagingData
                  ? (page, pageSize) =>
                      setPagingData({
                        ...pagingData,
                        page,
                        ...(pageSize && { pageSize }),
                      })
                  : void 0,
                ...pagination,
              }
            : false
        }
        dataSource={(data as PagedData<RecordType>)?.data || []}
        columns={
          isEmpty(filteredColumns)
            ? columns
            : columns!.filter((col: ColumnType<RecordType>) =>
                filteredColumns.find((c: ColumnType<RecordType>) => c.key === col.key)
              )
        }
        loading={
          loading
            ? {
                indicator: <LoadingOutlined spin />,
              }
            : false
        }
        {...rest}
      />
    </div>
  );
}
